import { ApolloClient, ApolloLink, InMemoryCache, Observable, createHttpLink, from, split } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { apm } from '@elastic/apm-rum';
import { ApolloClients, DefaultApolloClient, provideApolloClients } from '@vue/apollo-composable';
import sha256 from 'crypto-js/sha256';
import { print } from 'graphql';
import { createClient } from 'graphql-ws';
import { storeToRefs } from 'pinia';
import type { App } from 'vue';
import type { IGraphQlConfig } from '@/interface/IGraphQlConfig';
import { OddApiStatus } from '@/interface/enum';
import { formatDate } from '@/libraries/utils/date';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useOddsApiStore } from '@/stores/oddsApiStore';
import { useRootStore } from '@/stores/rootStore';

window.isUseTokenPrefix = false;

const TOKEN_HEADER = 'x-auth-token';

let unauthenticatedCount = 0;

const startTime = new Date().getTime();
function getPageOpenedMinutes() {
    try {
        return `${~~(((new Date().getTime()) - startTime) / 1000 / 60)}m`;
    } catch (error) {
        console.error('Failed to calculate page opened minutes:', error);
        return '--m';
    }
}

function debugLog(title: string, message: any) {
    const timeStamp = formatDate(new Date(), 'HH:mm:ss');
    console.warn(`[${timeStamp}][${getPageOpenedMinutes()}] ${title}: ${message}`);
}

const pendingResolves: Array<VoidFunction> = [];

async function refreshOddsApiTokens() {
    const { refreshOddsApiTokens: _refreshOddsApiTokens } = useOddsApiStore();
    try {
        await _refreshOddsApiTokens();
        pendingResolves.forEach(req => req());

        registerNextAutoRefreshToken();
    } finally {
        pendingResolves.length = 0;
    }
}

export function getAuthToken() {
    const { authToken, oddsApiStatus } = storeToRefs(useOddsApiStore());

    return new Promise<string>((resolve) => {
        switch (oddsApiStatus.value) {
            case OddApiStatus.LOADED:
                resolve(authToken.value);
                break;
            case OddApiStatus.LOADING:
                pendingResolves.push(() => resolve(authToken.value));
                break;
            case OddApiStatus.NO_TOKEN:
                pendingResolves.push(() => resolve(authToken.value));
                refreshOddsApiTokens();
                break;
            case OddApiStatus.ERRORED:
            default:
                throw Error('getAuthToken died');
        }
    });
}

window.addEventListener('offline', () => {
    if (__ENV_PROD__) debugLog('network', 'offline');
});

// update token when user [disconnected] -> [connected]
window.addEventListener('online', () => {
    if (__ENV_PROD__) debugLog('network', 'online');

    const { oddsApiStatus } = storeToRefs(useOddsApiStore());
    const { clearTokens } = useOddsApiStore();

    if (oddsApiStatus.value === OddApiStatus.ERRORED) {
        clearTokens();
        refreshOddsApiTokens();
    }
});

// update token when the page [invisible] -> [visible]
window.addEventListener('visibilitychange', () => {
    if (__ENV_PROD__) debugLog('visibility', document.visibilityState);

    const { oddsApiStatus } = storeToRefs(useOddsApiStore());
    const { clearTokens } = useOddsApiStore();

    if (document.visibilityState === 'visible' && oddsApiStatus.value === OddApiStatus.ERRORED) {
        clearTokens();
        refreshOddsApiTokens();
    }
});

let refreshTokenTimeout: number;
/**
 * Register a timeout(10 mins) to refresh token before it expires (15 mins)
 */
function registerNextAutoRefreshToken() {
    window.clearTimeout(refreshTokenTimeout);

    refreshTokenTimeout = window.setTimeout(() => {
        refreshOddsApiTokens();
    }, 10 * 60 * 1000); // 10 minutes
}

function setIsWsConnected(clientId: string, isConnected: boolean) {
    useRootStore().setIsWsConnected(isConnected);
}

const apolloClients: Record<string, ApolloClient<any>> = {};

export const createApolloClient = (config: IGraphQlConfig) => {
    if (apolloClients[config.clientId]) return apolloClients[config.clientId];

    const PREFIX = 'YplbcXy';
    const authLink = setContext(async () => {
        const authToken = await getAuthToken();
        const token = window.isUseTokenPrefix ? PREFIX + authToken : authToken;

        return {
            headers: {
                [TOKEN_HEADER]: token,
            },
        };
    });

    // eslint-disable-next-line consistent-return
    const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
        const { clearTokens } = useOddsApiStore();
        const { oddsApiStatus } = storeToRefs(useOddsApiStore());

        if (graphQLErrors) {
            const errorCode = graphQLErrors[0]?.extensions?.code ?? graphQLErrors[0]?.message;

            switch (errorCode) {
                case 'UNAUTHENTICATED':
                    debugLog('UNAUTHENTICATED', ++unauthenticatedCount);

                    if (oddsApiStatus.value === OddApiStatus.LOADING) {
                        break;
                    }
                    clearTokens();
                    break;
                case 'BAD_ENCODE_INPUT':
                    window.isUseTokenPrefix = true;
                    break;
                case 'BAD_TOKEN_INPUT':
                    window.isUseTokenPrefix = false;
                    break;
                case 'FORBIDDEN':
                    break;
                default: {
                    const gqlMessage = graphQLErrors[0]?.message;
                    const enriched = new Error(`[${operation.operationName}] GraphQL error: [${errorCode}] - ${gqlMessage}, variables: ${JSON.stringify(operation.variables)}`);
                    apm.captureError(enriched);
                    break;
                }
            }
        }

        if (networkError) {
            const statusCode = (networkError as { statusCode?: number }).statusCode;
            if (statusCode !== undefined && statusCode >= 400 && statusCode !== 401) {
                const enriched = new Error(`[${operation.operationName}] Network error: HTTP ${statusCode}, variables: ${JSON.stringify(operation.variables)}`);
                apm.captureError(enriched);
            }
        }

        return forward(operation);
    });

    const persistedQueryLink = createPersistedQueryLink({
        useGETForHashedQueries: true,
        generateHash: document => sha256(print(document)).toString(),
    });

    const retryLink = new RetryLink({
        delay: {
            initial: 200,
            max: 1000,
            jitter: true,
        },
        attempts: {
            max: 3,
            // retryIf: (error, _operation) => !!error,
        },
    });

    const httpLink = createHttpLink({
        uri: `${config.apiUrl}/${config.path}`,
        credentials: 'include',

    });
    const wsClient = config.wsUrl ? createClient({
        url: `${config.wsUrl}/${config.wsPath}`,
        connectionParams: async () => {
            const authToken = await getAuthToken();
            const token = window.isUseTokenPrefix ? PREFIX + authToken : authToken;
            // payload in connection_init
            return ({
                [TOKEN_HEADER]: token,
            });
        },
        shouldRetry: () => true,
        retryAttempts: Infinity,
        lazy: true,
        on: {
            error: () => {
                setIsWsConnected(config.clientId, false);
            },
            closed: () => {
                setIsWsConnected(config.clientId, false);
            },
            connecting: () => {
                setIsWsConnected(config.clientId, false);
            },
            connected: () => {
                setIsWsConnected(config.clientId, true);
            },
        },
    }) : null;

    let wsLink: ApolloLink;
    if (wsClient) {
        wsLink = new GraphQLWsLink(wsClient);
    } else {
        wsLink = new ApolloLink(() => new Observable(() => {}));
    }

    const requestLink = from([
        persistedQueryLink,
        httpLink,
    ]);

    const terminateLink = split(
        ({ query }) => {
            const definition = getMainDefinition(query);
            return definition.kind === 'OperationDefinition'
            && definition.operation === 'subscription';
        },
        wsLink,
        requestLink,
    );

    const apolloClient = new ApolloClient({
        link: from([
            errorLink,
            retryLink,
            authLink,
            terminateLink,
        ]),
        cache: new InMemoryCache({
            addTypename: false,
        }),
        defaultOptions: {
            query: {
                fetchPolicy: 'no-cache',
            },
            watchQuery: {
                fetchPolicy: 'no-cache',
            },
        },
        connectToDevTools: !__ENV_PROD__,
    });

    apolloClients[config.clientId] = apolloClient;

    return apolloClient;
};

export function setupApollo(app: App) {
    const { oddsApiUrl, oddsApiWsUrl } = useFeatureToggleStore();

    const oddsApiConfig = createApolloClient({
        clientId: 'oddsApi',
        isDefault: true,
        apiUrl: oddsApiUrl ?? '',
        wsUrl: oddsApiWsUrl ?? '',
        path: 'api',
        wsPath: 'ws',
        forceHttp: false,
    });

    provideApolloClients({
        default: oddsApiConfig,
        oddsApi: oddsApiConfig,
    });

    app.provide(DefaultApolloClient, oddsApiConfig);
    app.provide(ApolloClients, {
        default: oddsApiConfig,
        oddsApi: oddsApiConfig,
    });
}
